import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { ForgotPasswordRequest } from 'src/app/core/models';
import { AuthService } from 'src/app/core/services';
import { EncryptionService } from 'src/app/core/services/shared-services/encryption.service';
import { pattern } from 'src/app/shared/patterns';

@Component({
    selector: 'app-rest',
    templateUrl: './rest.component.html',
    styleUrls: ['./rest.component.scss'],
    standalone: false
})
export class RestComponent implements OnInit {
  public forgotForm: FormGroup;
  showForgotSubmitButtonLoader = false;
  emaiToken: any;
  parts: any;
  email: string = '';
  latestYear: number = new Date().getFullYear();
  PasswordForm: FormGroup;
  otpToken: any;
  upperCasePattern: RegExp = /[A-Z]/;
  lowerCasePattern: RegExp = /[a-z]/;
  digitPattern: RegExp = /[0-9]/;
  specialCharacterPattern: RegExp = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

  passwordRegex: string = '';
  passwordRules: any[] = [];

  minLength: number;
  maxLength: number;

  constructor(
    private messageService: MessageService,
    private authServiceApi: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private decryptService: EncryptionService,
    private translocoService: TranslocoService,
  ) {}

  getPasswordRegex() {
    this.authServiceApi.getPasswordRegex().subscribe({
      next: (res) => {
        console.log(res);
        if (res.data != null) {
          const regex = res.data;
  
          // Extract min and max length from the regex
          const lengthMatch = regex.match(/\{(\d+),(\d+)\}/);
          let validators = [Validators.required, Validators.pattern(regex)];
  
        const passwordControl = this.forgotForm.get('password');
        const confirmPasswordControl = this.forgotForm.get('confirmPassword');

        if (passwordControl && confirmPasswordControl) {
          passwordControl.setValidators(validators);
          confirmPasswordControl.setValidators(validators); // Same for 'confirmPassword'

          // Log the validators to confirm they are being set
          console.log('Password Validators:', passwordControl.validator);
          console.log('Confirm Password Validators:', confirmPasswordControl.validator);

          // Update the form validation
          passwordControl.updateValueAndValidity(); // Ensure the password control validates
          confirmPasswordControl.updateValueAndValidity(); // Ensure the confirm password control validates
        }
  
          // Generate rules for UI display
          this.generateRulesFromRegex(regex);
        }
      },
      error: (err) => console.log(err),
    });
  }
  passwordsMatchValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const passwordControl = formGroup.get('password');
      const newPasswordControl = formGroup.get('confirmPassword');
      if (!passwordControl || !newPasswordControl) {
        return null;
      }

      if (newPasswordControl.errors && !newPasswordControl.errors['passwordsMismatch']) {
        // If other validators have found an error on the newPasswordControl, do not overwrite them.
        return null;
      }

      if (passwordControl.value !== newPasswordControl.value) {
        newPasswordControl.setErrors({ passwordsMismatch: true });
        return { passwordsMismatch: true };
      } else {
        newPasswordControl.setErrors(null);
        return null;
      }
    };
  }

  ngOnInit() {
    this.forgotForm = new FormGroup(
      {
        password: new FormControl('', [
          Validators.required
        ]),
        confirmPassword: new FormControl('', [
          Validators.required
        ]),
      },
      { validators: this.passwordsMatchValidator() },
    );
    this.getPasswordRegex();
    this.route.queryParams.subscribe((params) => {
      for (const param in params) {
        if (params.hasOwnProperty(param)) {
          this.emaiToken = params['token'];
          if (this.emaiToken) {
            console.log('Token', this.emaiToken);
            let check = this.authServiceApi.jwtHelper.decodeToken(this.emaiToken);
            this.email = check.email;
            console.log('Decoded Token', check);
            let result = this.authServiceApi.jwtHelper.isTokenExpired(this.emaiToken);
            console.log('Token Expiry Result', result);
          } else {
            this.emaiToken = `${param}${params[param]}`;
            console.log('emaiToken', this.emaiToken);
            let check;
            check = this.authServiceApi.jwtHelper.decodeToken(this.emaiToken);
            this.email = check.email;
            console.log('check', check);
            let result;
            result = this.authServiceApi.jwtHelper.isTokenExpired(this.emaiToken);
            console.log('result', result);
          }
        }
      }
    });
  }

  forgotSubmit() {
    if (this.forgotForm.valid) {
      const request: ForgotPasswordRequest = {
        userName: this.email,
        newPassword: this.formForgotValues().password,
        confirmPassword: this.formForgotValues().confirmPassword,
        flowType: 'forgot',
        token: this.emaiToken,
        language: this.translocoService.getActiveLang(),
      };
      this.showForgotSubmitButtonLoader = true;
      this.authServiceApi.changePassword(request).subscribe({
        next: (v) => {
          if (v != null && v.success == true && v.data != null && v.data == true) {
            this.messageService.add({
              severity: 'success',
              summary: 'Password Change',
              detail: 'Password successfully changed',
            });
            this.router.navigate(['login']);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: v?.responseMessage_en ?? 'Unable to change password. Please try again.',
            });
          }

          this.showForgotSubmitButtonLoader = false;
        },
        error: (e) => {
          console.error(e);
          this.showForgotSubmitButtonLoader = false;
        },
      });
    }
  }

  showPassword: boolean = false;
  showHidePassword() {
    this.showPassword = !this.showPassword;
  }

  showConfirmPassword: boolean = false;
  showHideConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
  formForgotValues() {
    return this.forgotForm.value;
  }

  generateRulesFromRegex(regex: string): void {
    this.passwordRules = []; // Clear any previous rules

    // Length extraction (e.g., {15,32})
    const lengthMatch = regex.match(/\{(\d+),(\d+)\}/);
    if (lengthMatch) {
      const minLength = parseInt(lengthMatch[1], 10);
      const maxLength = parseInt(lengthMatch[2], 10);
      this.passwordRules.push(`Enter ${minLength} to ${maxLength} characters`);
      this.minLength = minLength;
      this.maxLength = maxLength;
    }

    // Check for uppercase letter
    if (regex.includes('(?=.*[A-Z])')) {
      this.passwordRules.push('At least one uppercase letter');
    }

    // Check for lowercase letter
    if (regex.includes('(?=.*[a-z])')) {
      this.passwordRules.push('At least one lowercase letter');
    }

    // Check for digit
    if (regex.includes('(?=.*\\d)')) {
      this.passwordRules.push('At least one number');
    }

    // Check for special characters
    if (regex.includes('(?=.*[@$!%?&#])')) {
      const specialChars = '@$!%?&#'; // List of special characters
      this.passwordRules.push(`At least one special character (${specialChars})`);
    }

    
  }

  isRuleValid(rule: string): boolean {
    const passwordValue = this.forgotForm.get('password').value || '';
   
    if (rule.includes('characters')) {
      return passwordValue.length >= this.minLength && passwordValue.length <= this.maxLength;
    }
  
    if (rule.includes('uppercase')) {
      return /[A-Z]/.test(passwordValue);
    }
  
    if (rule.includes('lowercase')) {
      return /[a-z]/.test(passwordValue);
    }
  
    if (rule.includes('number')) {
      return /\d/.test(passwordValue);
    }
  
    if (rule.includes('special character')) {
      return /[@$!%?&#]/.test(passwordValue);
    }
  
    return false;
  }
}
